import { useEffect, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    CircularProgress,
    Drawer,
    IconButton,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    useGetCarcinogenicPendingByCuitQuery,
    useGetCarcinogenicSrtHistorialQuery,
    useGetCarcinogenicSrtValidacionesLoteQuery
} from '../../../services/carcinogenicApi';
import {
    construirEstadosPorPaso,
    PASOS_SRT,
    EstadoPaso
} from '../../Presentaciones/components/srtHistorial';
import HallazgosEstablecimiento, {
    ErrorSvcc
} from '../../Presentaciones/components/HallazgosEstablecimiento';
import { EstadoCuitSvccChipValue } from '../../Presentaciones/components/PresentationStateChip';
import VigenciaBadge from './VigenciaBadge';

interface SVCCDetalleCuitDrawerProps {
    cuit: string;
    nombreOrganizacion?: string | null;
    estado?: EstadoCuitSvccChipValue;
    idPresentacionSrt?: number | null;
    fechaUltimoIntento?: string | null;
    fechaVencimientoEstimada?: string | null;
    cantidadEstablecimientos?: number;
    /**
     * Versión que la SRT le asignó a la presentación. Junto con el id es lo que
     * la identifica: el id se reusa entre períodos.
     */
    versionSrt?: number | null;
    onClose: () => void;
    onIniciar?: () => void;
    onConsolidar?: () => void;
    onCancelar?: () => void;
    onModificar?: () => void;
    onSincronizarNovedades?: () => void;
    sincronizandoNovedades?: boolean;
    /**
     * Detalle por informe que la grilla ya tiene: `POST /srt/cuit/validaciones`
     * lo devuelve junto al resumen, porque el backend lo calcula igual para
     * contar bloqueantes. Si viene, el drawer NO vuelve a validar — era la
     * segunda pasada del mismo validador sobre los mismos informes.
     */
    validacionesPrecargadas?: Record<number, ValidacionInforme> | null;
}

interface ValidacionInforme {
    completo: boolean;
    errores: ErrorSvcc[];
}

/**
 * Un color del theme y un color de Chip por estado de paso, en vez de una
 * paleta propia: los mismos verdes/rojos/azules que el resto de la app.
 */
const ESTILO_PASO: Record<
    EstadoPaso['estado'],
    {
        color: string;
        icono: string;
        etiqueta: string;
        chip: 'success' | 'error' | 'primary' | 'default';
    }
> = {
    done: { color: 'success.main', icono: '✓', etiqueta: 'Aceptado', chip: 'success' },
    error: { color: 'error.main', icono: '!', etiqueta: 'Rechazado', chip: 'error' },
    'in-progress': { color: 'primary.main', icono: '●', etiqueta: 'En curso', chip: 'primary' },
    pending: { color: 'transparent', icono: '', etiqueta: 'Pendiente', chip: 'default' }
};

/**
 * Un `severity` de Alert por estado, en vez de una paleta propia: el drawer usa
 * los mismos colores de aviso que el resto de la app.
 */
const BANNER_POR_ESTADO: Record<
    EstadoCuitSvccChipValue,
    { severidad: 'info' | 'success' | 'warning' | 'error'; titulo: string; texto: string }
> = {
    SIN_INICIAR: {
        severidad: 'info',
        titulo: 'Sin iniciar ante la SRT',
        texto: 'Al iniciar se envían todos los establecimientos del CUIT en un único lote.'
    },
    RECHAZADA: {
        severidad: 'error',
        titulo: 'El envío no se completó',
        texto: 'Un error en cualquier establecimiento frena todo el lote: hay que corregir y volver a iniciar desde cero. El detalle dice si el error fue de la SRT o del envío.'
    },
    PRESENTADA_SIN_CONSOLIDAR: {
        severidad: 'success',
        titulo: 'Los anexos fueron aceptados',
        texto: 'La presentación está abierta en la SRT. Falta consolidarla para cerrarla.'
    },
    CONSOLIDADA: {
        severidad: 'success',
        titulo: 'Presentación consolidada',
        texto: 'Para corregir datos ahora hay que usar Modificación de Anexo I / V / Nómina.'
    },
    DESINCRONIZADA: {
        severidad: 'warning',
        titulo: 'La SRT consolidó pero falló el guardado local',
        texto: 'Requiere intervención manual. No reintentar: la presentación ya está cerrada en la SRT.'
    }
};

const formatearCuit = (cuit: string) =>
    /^\d{11}$/.test(cuit) ? `${cuit.slice(0, 2)}-${cuit.slice(2, 10)}-${cuit.slice(10)}` : cuit;

const formatearFecha = (fecha?: string | null) => {
    if (!fecha) return '—';
    const valor = new Date(fecha);
    if (Number.isNaN(valor.getTime())) return '—';
    return valor.toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

// Mismos tokens que los drawers de carga del informe (`DrawerSvcc`).
const BORDE = '#E0EBED';
const TEXTO = '#1E272E';
const TEXTO_SUAVE = '#6B777C';
const FONDO_SUAVE = '#F3F8F8';

/** Chips de la lista de establecimientos: rellenos suaves, como los de la grilla. */
const CHIP_OK = { fondo: '#E3F5E3', texto: '#2D6830' };
const CHIP_ERROR = { fondo: '#FEF3F2', texto: '#B42318' };
const CHIP_AVISO = { fondo: '#FFF6E5', texto: '#B76E00' };
const CHIP_NEUTRO = { fondo: '#EEF2F3', texto: '#3D4A50' };

const ESTILO_BOTON = { textTransform: 'none', fontWeight: 600, borderRadius: '8px' } as const;
const ESTILO_AYUDA = {
    color: 'text.secondary',
    display: 'block',
    textAlign: 'center',
    mt: 0.75
} as const;

const Dato = ({ etiqueta, valor }: { etiqueta: string; valor: string }) => (
    <Box sx={{ bgcolor: FONDO_SUAVE, borderRadius: '10px', px: 1.5, py: 1, minWidth: 0 }}>
        <Typography
            variant="caption"
            sx={{
                color: TEXTO_SUAVE,
                letterSpacing: '.04em',
                display: 'block',
                fontSize: 10,
                fontWeight: 700
            }}
        >
            {etiqueta}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: TEXTO }} noWrap>
            {valor}
        </Typography>
    </Box>
);

/**
 * Línea de tiempo de los 5 pasos de un informe puntual. El mensaje de error de
 * la SRT sale de `log.errorMessage` del registro de historial: es el texto
 * crudo que devolvió el servicio, no un error del catálogo — la columna de
 * errores estructurados todavía no existe en `..._historial`.
 */
const TimelineInforme = ({ idInforme }: { idInforme: number }) => {
    const { data, isFetching } = useGetCarcinogenicSrtHistorialQuery(idInforme);

    if (isFetching) {
        return (
            <Box display="flex" alignItems="center" gap={1} sx={{ py: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="caption">Cargando historial…</Typography>
            </Box>
        );
    }

    const registros = data?.data ?? [];

    if (!registros.length) {
        return (
            <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                Todavía no hay intentos registrados contra la SRT para este informe.
            </Typography>
        );
    }

    const estadosPorPaso = construirEstadosPorPaso(registros);

    return (
        <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {PASOS_SRT.map((titulo, indice) => {
                const paso = estadosPorPaso[indice];
                const estilo = ESTILO_PASO[paso.estado];
                const esUltimo = indice === PASOS_SRT.length - 1;

                return (
                    <Box component="li" key={titulo} display="flex" gap={1.5}>
                        <Box display="flex" flexDirection="column" alignItems="center" flex="none">
                            <Box
                                sx={{
                                    width: 26,
                                    height: 26,
                                    borderRadius: '50%',
                                    bgcolor: estilo.color,
                                    color:
                                        paso.estado === 'pending'
                                            ? 'text.secondary'
                                            : 'common.white',
                                    border: paso.estado === 'pending' ? 2 : 0,
                                    borderColor: 'divider',
                                    display: 'grid',
                                    placeItems: 'center',
                                    fontSize: 12,
                                    fontWeight: 500
                                }}
                            >
                                {estilo.icono || indice + 1}
                            </Box>
                            {!esUltimo && (
                                <Box
                                    sx={{
                                        width: '2px',
                                        flex: 1,
                                        minHeight: 18,
                                        bgcolor: paso.estado === 'done' ? 'success.main' : 'divider'
                                    }}
                                />
                            )}
                        </Box>

                        <Box flex={1} pb={esUltimo ? 0 : 1.75}>
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                gap={1}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {`${indice + 1}. ${titulo}`}
                                </Typography>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    color={estilo.chip}
                                    label={estilo.etiqueta}
                                    sx={{ flex: 'none' }}
                                />
                            </Box>

                            {paso.errorMessage && (
                                <Alert severity="error" variant="outlined" sx={{ mt: 0.5 }}>
                                    {paso.errorCode && (
                                        <Typography
                                            variant="caption"
                                            sx={{ display: 'block', fontWeight: 700 }}
                                        >
                                            {paso.errorCode}
                                        </Typography>
                                    )}
                                    {paso.errorMessage}
                                </Alert>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

/**
 * Detalle de un CUIT desde la grilla de backoffice: en qué paso quedó la
 * presentación ante la SRT, qué error devolvió, y qué tiene para corregir cada
 * establecimiento del lote según la validación pre-envío.
 *
 * Se apoya en `/srt/cuit/:cuit/pendientes`, que devuelve los informes del CUIT
 * en estado RECIBIDO — que son exactamente los que pueden estar a mitad de
 * camino con la SRT. Un CUIT ya consolidado no tiene nada que revisar acá.
 */
const SVCCDetalleCuitDrawer = ({
    cuit,
    nombreOrganizacion,
    estado = 'SIN_INICIAR',
    idPresentacionSrt,
    fechaUltimoIntento,
    fechaVencimientoEstimada,
    cantidadEstablecimientos,
    versionSrt,
    onClose,
    onIniciar,
    onConsolidar,
    onCancelar,
    onModificar,
    onSincronizarNovedades,
    sincronizandoNovedades,
    validacionesPrecargadas
}: SVCCDetalleCuitDrawerProps) => {
    const { data, isFetching } = useGetCarcinogenicPendingByCuitQuery(cuit);
    const [informeSeleccionado, setInformeSeleccionado] = useState<number | null>(null);

    const establecimientos: any[] = Array.isArray(data?.data?.data)
        ? data.data.data
        : Array.isArray(data?.data)
          ? data.data
          : [];

    useEffect(() => {
        if (informeSeleccionado === null && establecimientos.length) {
            setInformeSeleccionado(establecimientos[0].idInforme);
        }
    }, [establecimientos, informeSeleccionado]);

    /**
     * `pendientes` devuelve los informes en RECIBIDO del CUIT. Con el trámite
     * abierto sin consolidar, ésos son justamente los que se enviaron (pasan a
     * PRESENTADO recién al consolidar), así que la validación pre-envío ya no
     * aporta: anticipa un rechazo que no va a pasar, y mostrar "3 advertencias"
     * al lado de un envío aceptado se lee como "hubo errores y pasó igual". El
     * panel cambia de sentido: es la lista de lo que entró en el lote.
     */
    const envioYaPaso = estado === 'PRESENTADA_SIN_CONSOLIDAR';

    /**
     * Con el CUIT ya consolidado es al revés: lo consolidado quedó en
     * PRESENTADO, así que todo lo que siga en RECIBIDO es un informe que NO
     * entró — típicamente un establecimiento cargado después. Ahí la validación
     * sí importa (es lo que va a necesitar la Modificación), y lo que hay que
     * decir es que quedó afuera.
     */
    const fueraDeLaConsolidada = estado === 'CONSOLIDADA' && establecimientos.length > 0;

    /**
     * Valida todo el lote en una sola llamada — una request por establecimiento
     * no escala. Si falla, no se bloquea nada de este lado: se avisa, porque un
     * panel vacío se lee igual que "no hay nada para corregir".
     *
     * Con `validacionesPrecargadas` no se pide nada: la grilla ya trae el
     * detalle por informe en la misma respuesta que usó para el resumen, y
     * pedirlo de nuevo era correr el mismo validador sobre los mismos informes.
     */
    const idsInformes = establecimientos.map((e: any) => e.idInforme).filter(Boolean);
    const hayPrecargadas = !!validacionesPrecargadas;

    const {
        data: respuestaValidaciones,
        isFetching: validando,
        isError: validacionesFallidas
    } = useGetCarcinogenicSrtValidacionesLoteQuery(idsInformes, {
        skip: envioYaPaso || hayPrecargadas || idsInformes.length === 0,
        // Cubre lo que cambió por fuera sin repetir el pedido en cada apertura.
        refetchOnMountOrArgChange: 60
    });

    const validaciones: Record<number, ValidacionInforme> =
        validacionesPrecargadas ?? respuestaValidaciones?.data ?? {};

    const banner = BANNER_POR_ESTADO[estado] ?? BANNER_POR_ESTADO.SIN_INICIAR;

    const totales = Object.values(validaciones).reduce(
        (acumulado, validacion) => {
            const errores = validacion?.errores ?? [];
            return {
                bloqueantes:
                    acumulado.bloqueantes + errores.filter((e) => e.severidad === 'error').length,
                advertencias:
                    acumulado.advertencias +
                    errores.filter((e) => e.severidad === 'advertencia').length,
                conObservaciones: acumulado.conObservaciones + (errores.length ? 1 : 0)
            };
        },
        { bloqueantes: 0, advertencias: 0, conObservaciones: 0 }
    );

    /**
     * Sin id de presentación no hay trámite abierto en la SRT: no hay nada que
     * cancelar y el CUIT se puede enviar de cero. Con id, la SRT no admite
     * sumar nada a una presentación cuyo estado ya cambió — hay que cancelarla.
     */
    const hayPresentacionAbierta = idPresentacionSrt != null;

    /**
     * Mismo criterio que la grilla: el envío se cierra SÓLO con bloqueantes
     * confirmados por la validación pre-envío. Un error en cualquier
     * establecimiento frena el lote entero, así que iniciar con bloqueantes es
     * un rechazo garantizado de la SRT — y encima deja el CUIT trabado con un
     * trámite abierto que hay que cancelar a mano.
     *
     * Mientras la validación viaja no se sabe nada y no se deshabilita: "todavía
     * no verifiqué" no es "las validaciones fallaron". Si la validación falla
     * (`validacionesFallidas`) tampoco se bloquea: la red de seguridad es el 400
     * de `vincular` con `bloqueados`.
     */
    const bloqueadoPorValidacion = totales.bloqueantes > 0;

    /*
     * El drawer valida el lote al abrirse. Mientras eso viaja no hay veredicto:
     * el botón de iniciar muestra el spinner y queda cerrado, igual que en la
     * grilla. `envioYaPaso` no valida nada (la SRT ya aceptó), así que ahí no hay
     * espera que mostrar.
     *
     * Incluye el `isFetching` de `pendientes` y no sólo el `validando` de la
     * mutación: la validación recién puede arrancar cuando se sabe qué informes
     * tiene el CUIT, así que mirando sólo la mutación el botón se habilitaba en
     * el hueco entre ambas requests y volvía a cerrarse — un parpadeo que además
     * deja pasar un click.
     */
    const esperandoValidacion = !envioYaPaso && (isFetching || validando);
    const motivoBloqueoValidacion = `${totales.bloqueantes} ${
        totales.bloqueantes === 1 ? 'error bloqueante' : 'errores bloqueantes'
    } en el lote: corregilos antes de enviar. El detalle está arriba, en Errores por establecimiento.`;

    /*
     * Sin informes pendientes no hay lote: la validación da 0 bloqueantes porque
     * no encuentra nada, y el botón quedaba habilitado para mandar un envío vacío
     * a la SRT. Pasa cuando el cliente tiene todos los informes en borrador,
     * corrigiéndolos. Lo mismo que cierra el botón de la grilla, pero acá sale de
     * los pendientes que el drawer ya trae.
     */
    const sinInformesParaEnviar = !envioYaPaso && !isFetching && establecimientos.length === 0;
    const motivoSinInformes =
        'No hay informes para enviar: el cliente los está corrigiendo y todavía no los reenvió.';

    const cta = (() => {
        if (estado === 'RECHAZADA' && !hayPresentacionAbierta)
            return {
                label: 'Iniciar presentación',
                onClick: onIniciar,
                color: 'primary' as const,
                variant: 'contained' as const,
                cargando: esperandoValidacion,
                deshabilitado:
                    !onIniciar ||
                    sinInformesParaEnviar ||
                    bloqueadoPorValidacion ||
                    esperandoValidacion,
                hint: esperandoValidacion
                    ? 'Validando el lote antes de habilitar el envío…'
                    : sinInformesParaEnviar
                      ? motivoSinInformes
                      : bloqueadoPorValidacion
                        ? motivoBloqueoValidacion
                        : 'No hay presentación abierta en la SRT: el envío arranca de cero con todos los establecimientos del CUIT.'
            };
        if (estado === 'SIN_INICIAR')
            return {
                label: 'Iniciar presentación',
                onClick: onIniciar,
                color: 'primary' as const,
                variant: 'contained' as const,
                cargando: esperandoValidacion,
                deshabilitado:
                    !onIniciar ||
                    sinInformesParaEnviar ||
                    bloqueadoPorValidacion ||
                    esperandoValidacion,
                hint: esperandoValidacion
                    ? 'Validando el lote antes de habilitar el envío…'
                    : sinInformesParaEnviar
                      ? motivoSinInformes
                      : bloqueadoPorValidacion
                        ? motivoBloqueoValidacion
                        : 'Se envían todos los establecimientos del CUIT. No se pueden presentar por separado.'
            };
        if (estado === 'RECHAZADA')
            return {
                label: 'Bloqueado — el cliente debe corregir',
                onClick: undefined,
                color: 'primary' as const,
                variant: 'contained' as const,
                deshabilitado: true,
                hint: 'Los errores ya quedan visibles para el cliente. No se puede reenviar hasta que cargue las correcciones.'
            };
        if (estado === 'PRESENTADA_SIN_CONSOLIDAR')
            return {
                label: 'Consolidar presentación',
                onClick: onConsolidar,
                color: 'success' as const,
                variant: 'contained' as const,
                // Consolidar sin trámite abierto tira en el backend: la
                // consolidación necesita el IdPresentacion de la SRT.
                deshabilitado: !onConsolidar || !hayPresentacionAbierta,
                hint: hayPresentacionAbierta
                    ? 'La consolidación cierra la presentación y no admite un segundo intento.'
                    : 'Sin presentación abierta en la SRT no hay nada que consolidar.'
            };
        if (estado === 'CONSOLIDADA')
            return {
                label: 'Modificar presentación',
                onClick: onModificar,
                color: 'primary' as const,
                variant: 'outlined' as const,
                deshabilitado: !onModificar,
                hint: 'Reenvía a la SRT los datos corregidos (Modificación de Anexo I / V / Nómina).'
            };
        return null;
    })();

    const puedeCancelar =
        Boolean(onCancelar) &&
        hayPresentacionAbierta &&
        (estado === 'RECHAZADA' || estado === 'PRESENTADA_SIN_CONSOLIDAR');

    /**
     * Consolidar no cierra el circuito: el empleador tiene 10 días corridos para
     * confirmar o rechazar, y hasta entonces no se puede modificar ni iniciar
     * otra presentación para el CUIT (Anexo IV §1.4;
     * `InformacionAdicionalART_V2.1.pdf` §3.2.2 y §3.7.2).
     *
     * Ese estado sólo llega por el WS de Novedades, y hasta ahora nada lo
     * consultaba: la presentación quedaba en "sin novedad" para siempre y el
     * CUIT trabado. Sólo se ofrece con la presentación consolidada, que es
     * cuando hay una novedad posible.
     */
    const puedeSincronizarNovedades = Boolean(onSincronizarNovedades) && estado === 'CONSOLIDADA';

    const tituloLista = envioYaPaso
        ? `ESTABLECIMIENTOS INCLUIDOS (${establecimientos.length})`
        : fueraDeLaConsolidada
          ? `ESTABLECIMIENTOS FUERA DE LA PRESENTACIÓN (${establecimientos.length})`
          : `ERRORES POR ESTABLECIMIENTO (${establecimientos.length})`;

    return (
        <Drawer
            anchor="right"
            open
            onClose={onClose}
            /*
             * El AppBar del layout vive en `theme.zIndex.drawer + 1`, asi que un
             * Drawer con su z-index por defecto (`theme.zIndex.drawer`) queda por
             * debajo del header y le tapa el titulo y los botones de accion.
             * Se sube apenas por encima del header, pero por debajo de
             * `theme.zIndex.modal` (1300), para que los dialogos de Reenviar /
             * Modificar / Consolidar —que se abren desde este mismo drawer y
             * siguen montados con el drawer abierto— queden por arriba.
             */
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
            /*
             * Misma cáscara que los drawers de carga del informe SVCC, pero a todo
             * el alto de la pantalla (va por encima del AppBar, ver el z-index de
             * arriba): esquinas redondeadas y sólo scrollea el cuerpo
             * (encabezado, datos y acción principal quedan a la vista).
             */
            PaperProps={{
                sx: {
                    height: '100%',
                    width: { xs: '100vw', sm: 600 },
                    borderRadius: { xs: 0, sm: '16px 0 0 16px' },
                    overflow: 'hidden',
                    boxShadow: '-8px 0 32px rgba(0,0,0,0.16)'
                }
            }}
        >
            <Box
                role="dialog"
                aria-label={`Detalle SRT del CUIT ${cuit}`}
                sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
            >
                <Box
                    component="header"
                    sx={{
                        px: 3,
                        pt: 2,
                        pb: 1.75,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.75,
                        borderBottom: `1px solid ${BORDE}`,
                        flexShrink: 0
                    }}
                >
                    <Box display="flex" alignItems="flex-start" gap={1.5}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '10px',
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: (theme) => alpha(theme.palette.primary.light, 0.14),
                                color: 'primary.main'
                            }}
                        >
                            <DescriptionOutlinedIcon fontSize="small" />
                        </Box>
                        <Box flex={1} minWidth={0}>
                            <Typography
                                component="h2"
                                sx={{
                                    fontSize: 18,
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                    color: TEXTO
                                }}
                            >
                                {nombreOrganizacion ?? 'Sin razón social'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: TEXTO_SUAVE, mt: 0.25 }}>
                                {`CUIT ${formatearCuit(cuit)}${
                                    cantidadEstablecimientos
                                        ? ` · ${cantidadEstablecimientos} establecimiento${
                                              cantidadEstablecimientos === 1 ? '' : 's'
                                          }`
                                        : ''
                                }`}
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="Cerrar"
                            onClick={onClose}
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '8px',
                                bgcolor: FONDO_SUAVE,
                                '&:hover': { bgcolor: BORDE }
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 18, color: '#3D4A50' }} />
                        </IconButton>
                    </Box>

                    {/*
                     * El id NO identifica una presentación puntual: la SRT lo
                     * reusa entre períodos para el mismo CUIT-ART
                     * (InformacionAdicionalART §3.2.3), y lo que avanza es la
                     * versión (§3.2.1). Por eso se muestra "id · vN" cuando
                     * conocemos la versión.
                     */}
                    <Box display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" gap={1}>
                        <Dato
                            etiqueta="PRESENTACIÓN SRT"
                            valor={
                                idPresentacionSrt
                                    ? `${idPresentacionSrt}${versionSrt ? ` · v${versionSrt}` : ''}`
                                    : '—'
                            }
                        />
                        <Dato
                            etiqueta="ÚLTIMO INTENTO"
                            valor={formatearFecha(fechaUltimoIntento)}
                        />
                        <Dato
                            etiqueta="ESTABLECIMIENTOS"
                            valor={
                                cantidadEstablecimientos !== undefined
                                    ? String(cantidadEstablecimientos)
                                    : '—'
                            }
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        px: 3,
                        py: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        '& .MuiAlert-root': { borderRadius: '12px' }
                    }}
                >
                    <Alert severity={banner.severidad} variant="outlined">
                        <AlertTitle sx={{ mb: 0.5 }}>{banner.titulo}</AlertTitle>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                            {banner.texto}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            <VigenciaBadge
                                fechaVencimientoEstimada={fechaVencimientoEstimada}
                                suprimido={estado === 'DESINCRONIZADA'}
                            />
                        </Box>
                    </Alert>

                    {isFetching && (
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={16} />
                            <Typography variant="body2">Cargando establecimientos…</Typography>
                        </Box>
                    )}

                    {!isFetching && !establecimientos.length && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            No hay informes de este CUIT pendientes de presentación ante la SRT.
                        </Typography>
                    )}

                    {fueraDeLaConsolidada && (
                        <Alert severity="warning" variant="outlined">
                            <AlertTitle sx={{ mb: 0.5 }}>
                                {establecimientos.length === 1
                                    ? 'Hay un establecimiento fuera de la presentación'
                                    : `Hay ${establecimientos.length} establecimientos fuera de la presentación`}
                            </AlertTitle>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                                La presentación de este CUIT ya se consolidó, y consolidar es
                                irreversible: estos informes no entraron. Sólo pueden sumarse por
                                Modificación de Anexo I / V / Nómina.
                            </Typography>
                        </Alert>
                    )}

                    {informeSeleccionado !== null && (
                        <Box
                            component="section"
                            sx={{ border: `1px solid ${BORDE}`, borderRadius: '12px', p: 2 }}
                        >
                            <Typography
                                component="h3"
                                sx={{ fontSize: 14, fontWeight: 700, color: TEXTO, mb: 1.5 }}
                            >
                                Proceso ante la SRT
                            </Typography>
                            <TimelineInforme idInforme={informeSeleccionado} />
                        </Box>
                    )}

                    {establecimientos.length > 0 && (
                        <Box
                            component="section"
                            sx={{
                                border: `1px solid ${BORDE}`,
                                borderRadius: '12px',
                                overflow: 'hidden',
                                flexShrink: 0
                            }}
                        >
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                gap={1}
                                flexWrap="wrap"
                                sx={{
                                    px: 2,
                                    py: 1.25,
                                    bgcolor: '#F7FAFA',
                                    borderBottom: `1px solid ${BORDE}`
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#5F6B70',
                                        fontWeight: 700,
                                        letterSpacing: '.04em'
                                    }}
                                >
                                    {tituloLista}
                                </Typography>
                                {validando && !envioYaPaso ? (
                                    // Validar el lote tarda: sin spinner, el
                                    // "Validando…" solo se lee como un cartel
                                    // estático y no queda claro que hay algo
                                    // en curso.
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={0.75}
                                        sx={{ flex: 'none' }}
                                    >
                                        <CircularProgress size={12} thickness={5} />
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'text.secondary' }}
                                        >
                                            Validando…
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {envioYaPaso
                                            ? 'La SRT aceptó el lote completo'
                                            : validacionesFallidas
                                              ? 'No se pudo validar el lote'
                                              : `${totales.conObservaciones} de ${establecimientos.length} con observaciones · ${totales.bloqueantes} bloqueantes · ${totales.advertencias} advertencias`}
                                    </Typography>
                                )}
                            </Box>

                            {establecimientos.map((establecimiento: any, indice: number) => {
                                const idInforme = establecimiento.idInforme;
                                const errores = validaciones[idInforme]?.errores ?? [];
                                const bloqueantes = errores.filter(
                                    (e) => e.severidad === 'error'
                                ).length;
                                const advertencias = errores.length - bloqueantes;
                                const abierto = informeSeleccionado === idInforme;

                                // Con el envío aceptado el badge deja de hablar
                                // de errores: lo único cierto de cada fila es
                                // que entró en el lote que la SRT tomó.
                                const badge = envioYaPaso
                                    ? 'Enviado'
                                    : validando
                                      ? 'Validando…'
                                      : bloqueantes
                                        ? `${bloqueantes} ${bloqueantes === 1 ? 'error' : 'errores'}`
                                        : advertencias
                                          ? `${advertencias} ${
                                                advertencias === 1 ? 'advertencia' : 'advertencias'
                                            }`
                                          : 'Sin errores';
                                const paletaBadge = envioYaPaso
                                    ? CHIP_OK
                                    : validando
                                      ? CHIP_NEUTRO
                                      : bloqueantes
                                        ? CHIP_ERROR
                                        : advertencias
                                          ? CHIP_AVISO
                                          : CHIP_OK;

                                return (
                                    <Box
                                        key={idInforme}
                                        sx={{
                                            borderTop: indice === 0 ? 'none' : `1px solid ${BORDE}`,
                                            bgcolor: abierto ? '#FBFCFC' : 'transparent'
                                        }}
                                    >
                                        <Box
                                            component="button"
                                            type="button"
                                            aria-label={`Establecimiento ${
                                                establecimiento.nombreEstablecimiento ?? idInforme
                                            }`}
                                            aria-expanded={abierto}
                                            onClick={() => setInformeSeleccionado(idInforme)}
                                            sx={{
                                                width: '100%',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                px: 2,
                                                py: 1.25,
                                                border: 'none',
                                                bgcolor: 'transparent',
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                font: 'inherit',
                                                '&:hover': { bgcolor: FONDO_SUAVE }
                                            }}
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                                minWidth={0}
                                            >
                                                {abierto ? (
                                                    <ExpandMoreIcon
                                                        fontSize="small"
                                                        sx={{ color: 'text.secondary' }}
                                                    />
                                                ) : (
                                                    <ChevronRightIcon
                                                        fontSize="small"
                                                        sx={{ color: 'text.secondary' }}
                                                    />
                                                )}
                                                <Box minWidth={0}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 600, color: TEXTO }}
                                                    >
                                                        {establecimiento.nombreEstablecimiento ??
                                                            `Informe ${idInforme}`}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ color: 'text.secondary' }}
                                                    >
                                                        {`Informe ${idInforme}`}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Chip
                                                size="small"
                                                label={badge}
                                                sx={{
                                                    flex: 'none',
                                                    height: 24,
                                                    borderRadius: '999px',
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    bgcolor: paletaBadge.fondo,
                                                    color: paletaBadge.texto
                                                }}
                                            />
                                        </Box>

                                        {abierto && !envioYaPaso && errores.length > 0 && (
                                            <Box sx={{ px: 2, pb: 1.5 }}>
                                                <HallazgosEstablecimiento errores={errores} />
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}

                    {(puedeSincronizarNovedades || puedeCancelar) && (
                        <Box
                            component="section"
                            sx={{
                                border: `1px solid ${BORDE}`,
                                borderRadius: '12px',
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.75
                            }}
                        >
                            <Typography
                                component="h3"
                                sx={{ fontSize: 14, fontWeight: 700, color: TEXTO }}
                            >
                                Otras acciones
                            </Typography>

                            {puedeSincronizarNovedades && (
                                <Box>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={onSincronizarNovedades}
                                        disabled={sincronizandoNovedades}
                                        sx={ESTILO_BOTON}
                                    >
                                        {sincronizandoNovedades
                                            ? 'Consultando la SRT…'
                                            : 'Sincronizar novedades de la SRT'}
                                    </Button>
                                    <Typography variant="caption" sx={ESTILO_AYUDA}>
                                        Consulta si el empleador confirmó la presentación. Hasta que
                                        la confirme —o pasen 10 días corridos— no se puede modificar
                                        ni iniciar otra para este CUIT.
                                    </Typography>
                                </Box>
                            )}

                            {puedeCancelar && (
                                <Box>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={onCancelar}
                                        color="error"
                                        sx={ESTILO_BOTON}
                                    >
                                        Cancelar presentación en la SRT
                                    </Button>
                                    <Typography variant="caption" sx={ESTILO_AYUDA}>
                                        {estado === 'PRESENTADA_SIN_CONSOLIDAR'
                                            ? 'Cancelar libera el CUIT y permite iniciar una presentación nueva. Después de consolidar ya no se puede.'
                                            : 'Cancelá la presentación abierta en la SRT para liberar el CUIT antes de volver a iniciar.'}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>

                {/* La acción principal queda fija abajo, como en los drawers de carga. */}
                {cta && (
                    <Box
                        component="footer"
                        sx={{
                            px: 3,
                            py: 1.5,
                            borderTop: `1px solid ${BORDE}`,
                            bgcolor: '#FFFFFF',
                            flexShrink: 0
                        }}
                    >
                        <Button
                            fullWidth
                            variant={cta.variant}
                            disabled={cta.deshabilitado}
                            onClick={cta.onClick}
                            color={cta.color}
                            startIcon={
                                'cargando' in cta && cta.cargando ? (
                                    <CircularProgress size={14} />
                                ) : undefined
                            }
                            sx={{ ...ESTILO_BOTON, height: 40 }}
                        >
                            {cta.label}
                        </Button>
                        <Typography variant="caption" sx={ESTILO_AYUDA}>
                            {cta.hint}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Drawer>
    );
};

export default SVCCDetalleCuitDrawer;
